import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { MenuItem, Stack, Typography } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import * as FormDialog from "../shared/FormDialog";
import { hasErrorCode } from "../api/errors";
import { Flag } from "../shared/Flag";
import { supportedLanguages, type SupportedLanguage } from "../api/userInfo";
import {
  createEnterpriseCustomerOrganisationMutationOptions,
  ENTERPRISE_CUSTOMER_COUNTRY_CODES,
  ENTERPRISE_CUSTOMER_ORGANISATION_NAME_ALREADY_EXISTS,
  type CreateEnterpriseCustomerOrganisationPayload,
  type EnterpriseCustomerCountryCode,
} from "../api/enterpriseCustomer";

export const CreateOrganisationDialog = ({
  defaultCountryCode,
  defaultLanguage,
  onClose,
}: {
  defaultCountryCode: EnterpriseCustomerCountryCode;
  defaultLanguage: SupportedLanguage;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const createProductAdminMutation = useMutation(createEnterpriseCustomerOrganisationMutationOptions());
  const {
    control,
    setFocus,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<CreateEnterpriseCustomerOrganisationPayload>({
    defaultValues: {
      organizationName: "",
      organizationNumber: "",
      countryCode: defaultCountryCode,
      language: defaultLanguage,
    },
  });

  FormDialog.useAutoFocusField(setFocus, "organizationName");

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Form
        onSubmit={handleSubmit(payload =>
          createProductAdminMutation.mutate(payload, {
            onSuccess: onClose,
            onError: error => {
              hasErrorCode(error, ENTERPRISE_CUSTOMER_ORGANISATION_NAME_ALREADY_EXISTS)
                ? setError(
                    "organizationName",
                    {
                      type: "organisationAlreadyExists",
                      message: t("EnterpriseCustomer.Organisations.NameAlreadyExists"),
                    },
                    { shouldFocus: true }
                  )
                : setError("root.serverError", { type: "fallback" });
            },
          })
        )}
      >
        <FormDialog.Title>{t("EnterpriseCustomer.Organisations.NewOrganisationDialog.Header")}</FormDialog.Title>
        <FormDialog.Content>
          <FormDialog.FormError isError={Boolean(errors?.root?.serverError)} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="organizationName"
              rules={{ required: t("Input.Validation.Required") }}
              label={t("EnterpriseCustomer.Field.Name")}
            />

            <FormDialog.TextField
              control={control}
              name="organizationNumber"
              rules={{ required: t("Input.Validation.Required") }}
              label={t("EnterpriseCustomer.Field.OrganisationNumber")}
            />

            <Stack spacing={2} direction="row">
              <FormDialog.TextField
                control={control}
                name="countryCode"
                label={t("EnterpriseCustomer.Field.Country")}
                select
              >
                {ENTERPRISE_CUSTOMER_COUNTRY_CODES.map(countryCode => (
                  <MenuItem key={countryCode} value={countryCode}>
                    <CountryValue countryCode={countryCode} />
                  </MenuItem>
                ))}
              </FormDialog.TextField>
              <FormDialog.TextField
                control={control}
                name="language"
                label={t("EnterpriseCustomer.Field.Language")}
                select
              >
                {supportedLanguages.map(language => (
                  <MenuItem key={language} value={language}>
                    <LanguageValue language={language} />
                  </MenuItem>
                ))}
              </FormDialog.TextField>
            </Stack>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={createProductAdminMutation.isPending}>
            {t("EnterpriseCustomer.Organisations.NewOrganisationDialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};

export const CountryValue = ({ countryCode }: { countryCode: EnterpriseCustomerCountryCode }) => {
  const { t } = useTranslation();

  return <FlaggedLabel flagCode={countryCode} label={t(`EnterpriseCustomer.Country.${countryCode}`)} />;
};

export const LanguageValue = ({ language }: { language: SupportedLanguage }) => {
  const { t } = useTranslation();

  return <FlaggedLabel flagCode={languageFlagCodes[language]} label={t(`EnterpriseCustomer.Language.${language}`)} />;
};

const languageFlagCodes: Record<SupportedLanguage, string> = { sv: "SE", no: "NO", en: "GB" };

const FlaggedLabel = ({ flagCode, label }: { flagCode: string; label: string }) => (
  <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
    <Flag countryCode={flagCode} />
    <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14) }}>{label}</Typography>
  </Stack>
);
